import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { editarJson, enviarFormulario, enviarJson, enviarSemConteudo, pedirJson } from "./api";

const esquemaAcao = z.object({
  id: z.string().uuid(),
  tipo: z.enum(["publica", "interna"]),
  texto: z.string(),
  autor_id: z.string().uuid().nullable(),
  autor_email: z.string().nullable(),
  canal: z.string(),
  criada_em: z.string(),
  /* Número do chamado de origem quando a ação veio de um chamado mesclado neste. */
  origem_numero: z.number().int().positive().nullable().optional(),
});

const esquemaEvento = z.object({
  id: z.string().uuid(),
  tipo: z.string(),
  detalhe: z.record(z.string(), z.unknown()),
  ator_id: z.string().uuid().nullable(),
  ator_email: z.string().nullable(),
  ocorrido_em: z.string(),
});

const esquemaAnexo = z.object({
  id: z.string().uuid(),
  acao_id: z.string().uuid().nullable(),
  nome: z.string(),
  mime: z.string(),
  tamanho: z.number().int().nonnegative(),
  interno: z.boolean(),
  criado_em: z.string(),
  origem_numero: z.number().int().positive().nullable().optional(),
});

const esquemaEquipe = z.object({ id: z.string().uuid(), nome: z.string() });
const esquemaAgente = z.object({
  id: z.string().uuid(),
  nome: z.string(),
  email: z.string(),
});
const esquemaCategoria = z.object({ id: z.string().uuid(), nome: z.string() });
const esquemaServico = z.object({
  id: z.string().uuid(),
  categoria_id: z.string().uuid(),
  nome: z.string(),
  /* O desenho da restrição (catálogo restrito, 0092): só a gestão do catálogo recebe `true`. */
  restrito_a_organizacoes: z.boolean().optional(),
  restrito_a_equipes: z.boolean().optional(),
});

/* A bandeira da equipe num chamado (cor e motivo). Só chega ao lado interno. */
export const esquemaSinalizador = z.object({
  cor: z.enum(["vermelho", "laranja", "amarelo", "verde", "azul", "roxo"]),
  motivo: z.string(),
  definido_por: z.string().uuid().nullable(),
  definido_em: z.string(),
});
const esquemaSla = z.object({
  politica_id: z.string().uuid(),
  // `null` quando o prazo está violado e quem lê é o cliente: o veredito é interno.
  estado: z
    .enum(["sem_politica", "no_prazo", "proximo_do_limite", "pausado", "violado", "cumprido"])
    .nullable(),
  primeira_resposta_limite_em: z.string().nullable(),
  solucao_limite_em: z.string().nullable(),
  primeira_resposta_em: z.string().nullable(),
  solucao_em: z.string().nullable(),
  pausado_em: z.string().nullable(),
  pausa_acumulada_segundos: z.number().int().nonnegative(),
  sobrescrito_em: z.string().nullable(),
  sobrescrito_por: z.string().uuid().nullable(),
  sobrescricao_motivo: z.string().nullable(),
});
const esquemaContratoDeSla = z.object({
  id: z.string().uuid(),
  organizacao_id: z.string().uuid(),
  nome: z.string(),
  padrao: z.boolean(),
});
const esquemaPoliticaDeSla = z.object({
  id: z.string().uuid(),
  organizacao_id: z.string().uuid(),
  nome: z.string(),
  urgencia: z.enum(["baixa", "media", "alta", "urgente"]).nullable(),
  categoria: z.string().nullable(),
  servico: z.string().nullable(),
  contrato_id: z.string().uuid().nullable(),
  calendario_id: z.string().uuid().nullable(),
  primeira_resposta_minutos: z.number().int().positive().nullable(),
  solucao_minutos: z.number().int().positive().nullable(),
  alerta_antes_minutos: z.number().int().nonnegative().nullable(),
  escalonamento_apos_minutos: z.number().int().nonnegative().nullable(),
});
const esquemaCalendarioDeSla = z.object({
  id: z.string().uuid(),
  organizacao_id: z.string().uuid(),
  nome: z.string(),
  fuso_horario: z.string(),
  dias_da_semana: z.array(z.number().int().min(0).max(6)),
  hora_inicio: z.string(),
  hora_fim: z.string(),
  feriados: z.array(z.string()),
});
const esquemaPainelDeSla = z.object({
  total: z.number().int().nonnegative(),
  no_prazo: z.number().int().nonnegative(),
  proximo_do_limite: z.number().int().nonnegative(),
  pausado: z.number().int().nonnegative(),
  violado: z.number().int().nonnegative(),
  cumprido: z.number().int().nonnegative(),
  sem_politica: z.number().int().nonnegative(),
  primeira_resposta_medida: z.number().int().nonnegative(),
  primeira_resposta_no_prazo: z.number().int().nonnegative(),
  primeira_resposta_cumprimento_percentual: z.number().nonnegative().max(100).nullable(),
  solucao_medida: z.number().int().nonnegative(),
  solucao_no_prazo: z.number().int().nonnegative(),
  solucao_cumprimento_percentual: z.number().nonnegative().max(100).nullable(),
  alertas_abertos: z.number().int().nonnegative(),
  alertas: z.array(z.object({
    id: z.string().uuid(),
    ticket_id: z.string().uuid(),
    numero_do_ticket: z.number().int().positive(),
    assunto_do_ticket: z.string(),
    organizacao: z.string(),
    marco: z.enum(["primeira_resposta", "solucao"]),
    tipo: z.enum(["aproximacao", "vencimento", "escalonamento"]),
    limite_em: z.string(),
    gerado_em: z.string(),
  })),
  tickets: z.array(z.object({
    id: z.string().uuid(),
    numero: z.number().int().positive(),
    organizacao_id: z.string().uuid(),
    organizacao: z.string(),
    assunto: z.string(),
    servico: z.string().nullable(),
    equipe_id: z.string().uuid().nullable(),
    responsavel_id: z.string().uuid().nullable(),
    criado_em: z.string(),
    estado_do_ticket: z.string(),
    estado_do_sla: z.string(),
    primeira_resposta_limite_em: z.string().nullable(),
    solucao_limite_em: z.string().nullable(),
    primeira_resposta_em: z.string().nullable(),
    solucao_em: z.string().nullable(),
  })),
});

const esquemaCondicaoDeCampo = z.object({ campo_id: z.string().uuid(), valor: z.unknown() });

/** Um campo personalizado que vale para o chamado, com o valor atual (`null` = não preenchido). */
export const esquemaCampoDoChamado = z.object({
  campo_id: z.string().uuid(),
  rotulo: z.string(),
  tipo: z.enum(["texto", "numero", "data", "lista", "multipla", "sim_nao"]),
  opcoes: z.array(z.string()),
  obrigatorio: z.boolean(),
  visivel_ao_cliente: z.boolean(),
  condicao: esquemaCondicaoDeCampo.nullable(),
  valor: z.unknown().nullable().optional(),
});
export type CampoDoChamado = z.infer<typeof esquemaCampoDoChamado>;

/* O formulário de chamado do cliente (item 11) e, só para a equipe, o espelho no ATD. */
const esquemaEspelhoNoAtd = z.object({
  estado: z.enum(["pendente", "criado", "falhou"]),
  chave: z.string().nullable(),
  ultimo_erro: z.string().nullable(),
  tentativas: z.number().int().nonnegative(),
  anexos_pendentes: z.number().int().nonnegative(),
  anexos_erro: z.string().nullable(),
  edicao_pendente: z.boolean(),
  edicao_erro: z.string().nullable(),
});
export type EspelhoNoAtd = z.infer<typeof esquemaEspelhoNoAtd>;

const esquemaFormularioDoChamado = z.object({
  modulo_chave: z.string().nullable(),
  modulo: z.string().nullable(),
  tipo: z.enum(["erro", "duvida", "ajuste", "novo_recurso"]),
  tipo_rotulo: z.string(),
  impacto: z.enum(["atrapalha", "uma_pessoa", "todos"]),
  impacto_rotulo: z.string(),
  ambiente: z.string().nullable(),
  versao: z.string().nullable(),
  responsavel_ti: z.string().nullable(),
  espelho: esquemaEspelhoNoAtd.nullable().optional(),
});
export type FormularioDoChamado = z.infer<typeof esquemaFormularioDoChamado>;

export const esquemaTicket = z.object({
  id: z.string().uuid(),
  numero: z.number().int().positive(),
  organizacao_id: z.string().uuid(),
  solicitante_id: z.string().uuid().nullable(),
  responsavel_id: z.string().uuid().nullable(),
  equipe_id: z.string().uuid().nullable(),
  assunto: z.string(),
  descricao: z.string(),
  tipo: z.enum(["publico", "interno"]),
  estado: z.enum(["novo", "em_atendimento", "parado", "resolvido", "fechado", "cancelado"]),
  urgencia: z.enum(["baixa", "media", "alta", "urgente"]),
  categoria: z.string().nullable(),
  servico: z.string().nullable(),
  criado_em: z.string(),
  atualizado_em: z.string(),
  resolvido_em: z.string().nullable(),
  mesclado_em_id: z.string().uuid().nullable().optional(),
  mesclado_em_numero: z.number().int().positive().nullable().optional(),
  sla: esquemaSla.nullable(),
  campos: z.array(esquemaCampoDoChamado).optional(),
  acoes: z.array(esquemaAcao),
  anexos: z.array(esquemaAnexo),
  eventos: z.array(esquemaEvento),
  /* Marcas da equipe: o favorito é de quem lê, o sinalizador é de todos da equipe. */
  favorito: z.boolean().optional(),
  sinalizador: esquemaSinalizador.nullable().optional(),
  /* Só em chamado aberto pelo formulário do cliente, e só no detalhe. */
  formulario: esquemaFormularioDoChamado.nullable().optional(),
});

export type Ticket = z.infer<typeof esquemaTicket>;
export type EstadoDoTicket = Ticket["estado"];
export type UrgenciaDoTicket = Ticket["urgencia"];
export type EquipeDeAtendimento = z.infer<typeof esquemaEquipe>;
export type AgenteDeAtendimento = z.infer<typeof esquemaAgente>;
export type CategoriaDeTicket = z.infer<typeof esquemaCategoria>;
export type ServicoDeTicket = z.infer<typeof esquemaServico>;
export type PoliticaDeSla = z.infer<typeof esquemaPoliticaDeSla>;
export type ContratoDeSla = z.infer<typeof esquemaContratoDeSla>;
export type CalendarioDeSla = z.infer<typeof esquemaCalendarioDeSla>;

export interface FiltrosDeTickets {
  texto?: string;
  estado?: EstadoDoTicket | "";
  urgencia?: UrgenciaDoTicket | "";
  equipe_id?: string;
  responsavel_id?: string;
  categoria?: string;
  tag?: string;
  campo_id?: string;
  campo_valor?: string;
  /** `"true"` mostra só os chamados que a pessoa favoritou. */
  favoritos?: string;
  /** Uma cor, ou `qualquer` para todo chamado sinalizado. */
  sinalizador?: string;
}

export interface FiltrosDoPainelDeSla {
  inicio?: string;
  fim?: string;
  organizacao_id?: string;
  servico?: string;
  equipe_id?: string;
  responsavel_id?: string;
}

const CHAVE = ["tickets"] as const;
const chaveDoTicket = (id: string) => [...CHAVE, id] as const;

function invalidar(cliente: ReturnType<typeof useQueryClient>, id?: string) {
  void cliente.invalidateQueries({ queryKey: CHAVE });
  if (id) void cliente.invalidateQueries({ queryKey: chaveDoTicket(id) });
}

export function useTickets(filtros: FiltrosDeTickets = {}) {
  const parametros = new URLSearchParams();
  for (const [chave, valor] of Object.entries(filtros)) {
    if (valor) parametros.set(chave, valor);
  }
  const consulta = parametros.toString();
  return useQuery({
    queryKey: [...CHAVE, filtros],
    queryFn: ({ signal }) =>
      pedirJson(`/tickets${consulta ? `?${consulta}` : ""}`, z.array(esquemaTicket), { signal }),
  });
}

export function useTicket(id: string) {
  return useQuery({
    queryKey: chaveDoTicket(id),
    queryFn: ({ signal }) => pedirJson(`/tickets/${encodeURIComponent(id)}`, esquemaTicket, { signal }),
    enabled: Boolean(id),
  });
}

export function useEquipesDeAtendimento(enabled = true) {
  return useQuery({
    queryKey: [...CHAVE, "equipes"],
    queryFn: ({ signal }) => pedirJson("/tickets/equipes", z.array(esquemaEquipe), { signal }),
    enabled,
  });
}

export function useAgentesDeAtendimento(enabled = true) {
  return useQuery({
    queryKey: [...CHAVE, "agentes"],
    queryFn: ({ signal }) => pedirJson("/tickets/agentes", z.array(esquemaAgente), { signal }),
    enabled,
  });
}

export function useCriarEquipeDeAtendimento() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (nome: string) => enviarJson("/tickets/equipes", esquemaEquipe, { nome }),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: [...CHAVE, "equipes"] }),
  });
}

export function useCatalogoDeTickets() {
  const categorias = useQuery({
    queryKey: [...CHAVE, "catalogo", "categorias"],
    queryFn: ({ signal }) =>
      pedirJson("/tickets/catalogo/categorias", z.array(esquemaCategoria), { signal }),
  });
  const servicos = useQuery({
    queryKey: [...CHAVE, "catalogo", "servicos"],
    queryFn: ({ signal }) =>
      pedirJson("/tickets/catalogo/servicos", z.array(esquemaServico), { signal }),
  });
  return { categorias, servicos };
}

export function useCriarCategoriaDeTicket() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (nome: string) =>
      enviarJson("/tickets/catalogo/categorias", esquemaCategoria, { nome }),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: [...CHAVE, "catalogo"] }),
  });
}

export function useCriarServicoDeTicket() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ categoriaId, nome }: { categoriaId: string; nome: string }) =>
      enviarJson("/tickets/catalogo/servicos", esquemaServico, {
        categoria_id: categoriaId,
        nome,
      }),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: [...CHAVE, "catalogo"] }),
  });
}

export function usePainelDeSla(filtros: FiltrosDoPainelDeSla = {}, enabled = true) {
  const parametros = new URLSearchParams();
  for (const [chave, valor] of Object.entries(filtros)) {
    if (valor) parametros.set(chave, valor);
  }
  const consulta = parametros.toString();
  return useQuery({
    queryKey: [...CHAVE, "sla", "painel", filtros],
    queryFn: ({ signal }) =>
      pedirJson(`/tickets/sla/painel${consulta ? `?${consulta}` : ""}`, esquemaPainelDeSla, { signal }),
    enabled,
    refetchInterval: enabled ? 60_000 : false,
  });
}

export function useReconhecerAlertaDeSla() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (alertaId: string) =>
      enviarSemConteudo(`/tickets/sla/alertas/${encodeURIComponent(alertaId)}/reconhecer`),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: [...CHAVE, "sla", "painel"] }),
  });
}

export function usePoliticasDeSla(enabled = true) {
  return useQuery({
    queryKey: [...CHAVE, "sla", "politicas"],
    queryFn: ({ signal }) =>
      pedirJson("/tickets/sla/politicas", z.array(esquemaPoliticaDeSla), { signal }),
    enabled,
  });
}

export function useContratosDeSla(enabled = true) {
  return useQuery({
    queryKey: [...CHAVE, "sla", "contratos"],
    queryFn: ({ signal }) =>
      pedirJson("/tickets/sla/contratos", z.array(esquemaContratoDeSla), { signal }),
    enabled,
  });
}

export function useCriarContratoDeSla() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: { organizacao_id: string; nome: string; padrao: boolean }) =>
      enviarJson("/tickets/sla/contratos", esquemaContratoDeSla, corpo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: [...CHAVE, "sla"] }),
  });
}

export function useCalendariosDeSla(enabled = true) {
  return useQuery({
    queryKey: [...CHAVE, "sla", "calendarios"],
    queryFn: ({ signal }) =>
      pedirJson("/tickets/sla/calendarios", z.array(esquemaCalendarioDeSla), { signal }),
    enabled,
  });
}

export function useCriarCalendarioDeSla() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: {
      organizacao_id: string;
      nome: string;
      fuso_horario: string;
      dias_da_semana: number[];
      hora_inicio: string;
      hora_fim: string;
      feriados: string[];
    }) => enviarJson("/tickets/sla/calendarios", esquemaCalendarioDeSla, corpo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: [...CHAVE, "sla"] }),
  });
}

export function useCriarPoliticaDeSla() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: {
      organizacao_id: string;
      nome: string;
      urgencia: UrgenciaDoTicket | null;
      categoria: string | null;
      servico: string | null;
      contrato_id: string | null;
      calendario_id: string | null;
      primeira_resposta_minutos: number | null;
      solucao_minutos: number | null;
      alerta_antes_minutos: number | null;
      escalonamento_apos_minutos: number | null;
    }) => enviarJson("/tickets/sla/politicas", esquemaPoliticaDeSla, corpo),
    onSuccess: () => invalidar(cliente),
  });
}

export function useSobrescreverSla(ticketId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: {
      primeira_resposta_minutos: number | null;
      solucao_minutos: number | null;
      motivo: string;
    }) => enviarJson(`/tickets/${encodeURIComponent(ticketId)}/sla/sobrescrever`, esquemaSla, corpo),
    onSuccess: () => invalidar(cliente, ticketId),
  });
}

export function useRestaurarSlaAutomatico(ticketId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: () =>
      enviarJson(`/tickets/${encodeURIComponent(ticketId)}/sla/automatico`, esquemaSla, {}),
    onSuccess: () => invalidar(cliente, ticketId),
  });
}

export function useAbrirTicket() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: {
      assunto: string;
      descricao: string;
      urgencia: UrgenciaDoTicket;
      /* Valores dos campos personalizados, por id do campo. */
      campos?: Record<string, unknown>;
    }) => enviarJson("/tickets", esquemaTicket, corpo),
    onSuccess: (ticket) => invalidar(cliente, ticket.id),
  });
}

const esquemaOpcao = z.object({
  chave: z.string(),
  rotulo: z.string(),
  no_jira: z.string().nullable().optional(),
});
const esquemaOpcoesDoFormulario = z.object({
  modulos: z.array(esquemaOpcao),
  tipos: z.array(esquemaOpcao),
  impactos: z.array(esquemaOpcao),
  origem: z.string(),
  canal_de_entrada: z.string(),
  automatico: z.object({
    empresa: z.string(),
    quem_nome: z.string(),
    quem_email: z.string(),
    ambiente: z.string().nullable(),
    versao: z.string().nullable(),
    responsavel_ti: z.string().nullable(),
  }),
  anexos: z.object({
    maximo: z.number().int().positive(),
    maximo_de_videos: z.number().int().positive(),
    imagem_bytes: z.number().int().positive(),
    video_bytes: z.number().int().positive(),
    total_bytes: z.number().int().positive(),
    formatos: z.array(z.string()),
  }),
});
export type OpcoesDoFormulario = z.infer<typeof esquemaOpcoesDoFormulario>;

/** O que a tela oferece (módulos contratados, tipos, impactos) e o que o portal preenche sozinho. */
export function useOpcoesDoFormulario(habilitado = true) {
  return useQuery({
    queryKey: [...CHAVE, "formulario", "opcoes"],
    queryFn: ({ signal }) =>
      pedirJson("/tickets/formulario/opcoes", esquemaOpcoesDoFormulario, { signal }),
    enabled: habilitado,
  });
}

export interface PedidoDoFormulario {
  tipo: string;
  impacto: string;
  modulo: string;
  descricao: string;
  categoria?: string;
  servico?: string;
  /* Valores dos campos personalizados, por id do campo. */
  campos?: Record<string, unknown>;
  anexos: readonly File[];
}

/** Abre o chamado pelo formulário do cliente (multipart: o texto e os prints/vídeos juntos). */
export function useAbrirChamadoPeloFormulario() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (pedido: PedidoDoFormulario) => {
      const formulario = new FormData();
      formulario.set("tipo", pedido.tipo);
      formulario.set("impacto", pedido.impacto);
      formulario.set("modulo", pedido.modulo);
      formulario.set("descricao", pedido.descricao);
      if (pedido.categoria) formulario.set("categoria", pedido.categoria);
      if (pedido.servico) formulario.set("servico", pedido.servico);
      if (pedido.campos && Object.keys(pedido.campos).length > 0) {
        formulario.set("campos", JSON.stringify(pedido.campos));
      }
      for (const arquivo of pedido.anexos) formulario.append("anexos", arquivo);
      return enviarFormulario("/tickets/formulario", esquemaTicket, formulario);
    },
    onSuccess: (ticket) => invalidar(cliente, ticket.id),
  });
}

/** Reprocessa a issue do ATD deste chamado (equipe). */
export function useReprocessarIssueDoAtd(id: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: () =>
      enviarJson(`/tickets/${encodeURIComponent(id)}/jira/reprocessar`, esquemaEspelhoNoAtd, {}),
    onSuccess: () => invalidar(cliente, id),
  });
}

export function useResponderTicket(id: string, interna = false) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (texto: string) =>
      enviarJson(
        `/tickets/${encodeURIComponent(id)}/${interna ? "notas" : "acoes"}`,
        esquemaAcao,
        { texto },
      ),
    onSuccess: () => invalidar(cliente, id),
  });
}

export function useAlterarTicket(id: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (campos: {
      estado?: EstadoDoTicket;
      equipe_id?: string | null;
      responsavel_id?: string | null;
      categoria?: string | null;
      servico?: string | null;
    }) => editarJson(`/tickets/${encodeURIComponent(id)}`, esquemaTicket, campos),
    onSuccess: () => invalidar(cliente, id),
  });
}

export function useAnexarAoTicket(id: string, interno = false) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (arquivo: File) => {
      const formulario = new FormData();
      formulario.append("arquivo", arquivo);
      formulario.append("interno", String(interno));
      return enviarFormulario(`/tickets/${encodeURIComponent(id)}/anexos`, esquemaAnexo, formulario);
    },
    onSuccess: () => invalidar(cliente, id),
  });
}

export function caminhoDoAnexoDoTicket(ticketId: string, anexoId: string): string {
  return `/api/tickets/${encodeURIComponent(ticketId)}/anexos/${encodeURIComponent(anexoId)}`;
}

export const ROTULO_DO_ESTADO: Record<EstadoDoTicket, string> = {
  novo: "Novo",
  em_atendimento: "Em atendimento",
  parado: "Aguardando",
  resolvido: "Resolvido",
  fechado: "Fechado",
  cancelado: "Cancelado",
};

/* De onde veio uma mensagem do chamado. O portal é o canal de sempre e não leva marca; só o que
   chegou de fora (e-mail, formulário público) é dito na conversa. Canal que a API passe a gravar
   sem frase aqui também não aparece: nunca a chave técnica na tela. */
const ROTULO_DO_CANAL: Readonly<Record<string, string>> = {
  email: "por e-mail",
  formulario_publico: "pelo formulário público",
};

export function rotuloDoCanal(canal: string): string | null {
  return ROTULO_DO_CANAL[canal] ?? null;
}

export const ROTULO_DA_URGENCIA: Record<UrgenciaDoTicket, string> = {
  baixa: "Baixa",
  media: "Média",
  alta: "Alta",
  urgente: "Urgente",
};
